const TAG = 'sardius';

type LogFields = Record<string, unknown>;

interface DebugLogger {
  debug(tag: string, ...args: unknown[]): void;
  info(tag: string, ...args: unknown[]): void;
  warn(tag: string, ...args: unknown[]): void;
  error(tag: string, ...args: unknown[]): void;
}

let debugLogger: DebugLogger | null = null;

export function initLogger(): void {
  // Production receivers keep to console logs; see the dev-debug-logger plugin.
  if (!import.meta.env.DEV) return;
  try {
    const instance = cast.debug.CastDebugLogger.getInstance();
    instance.setEnabled(true);
    instance.showDebugLogs(true);
    debugLogger = instance;
  } catch {
    debugLogger = null;
  }
}

function formatValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'string') {
    return /[\s"=]/.test(v) ? JSON.stringify(v) : v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function fmt(category: string, message: string, fields?: LogFields): string {
  const head = `[${TAG}:${category}] ${message}`;
  if (!fields) return head;
  const entries = Object.entries(fields);
  if (entries.length === 0) return head;
  const pairs = entries.map(([k, v]) => `${k}=${formatValue(v)}`).join(' ');
  return `${head} ${pairs}`;
}

export const log = {
  info(category: string, message: string, fields?: LogFields): void {
    const line = fmt(category, message, fields);
    debugLogger?.info(TAG, line);
    console.info(line);
  },
  warn(category: string, message: string, fields?: LogFields): void {
    const line = fmt(category, message, fields);
    debugLogger?.warn(TAG, line);
    console.warn(line);
  },
  error(category: string, message: string, fields?: LogFields): void {
    const line = fmt(category, message, fields);
    debugLogger?.error(TAG, line);
    console.error(line);
  },
  debug(category: string, message: string, fields?: LogFields): void {
    const line = fmt(category, message, fields);
    debugLogger?.debug(TAG, line);
    if (import.meta.env.DEV) console.debug(line);
  },
};

/**
 * Banner printed once at receiver startup. Helps disambiguate which build is
 * actually running on the device when comparing logs across redeploys.
 */
export function logStartupBanner(extra?: LogFields): void {
  // Big, visually distinct banner that survives the noise of CAF SDK logs.
  // Match the `build=` value against `git log -1 --pretty=%h` to confirm
  // which commit the device actually loaded.
  const stripe = '═══════════════════════════════════════════════';
  /* eslint-disable no-console */
  console.log(stripe);
  console.log(`  Sardius CAFv3 Receiver`);
  console.log(`  build  ${__COMMIT_SHA__}  (v${__APP_VERSION__})`);
  console.log(`  built  ${__BUILD_TIME__}`);
  console.log(stripe);
  /* eslint-enable no-console */

  const fields: LogFields = {
    version: __APP_VERSION__,
    build: __COMMIT_SHA__,
    buildTime: __BUILD_TIME__,
    mode: import.meta.env.DEV ? 'dev' : 'prod',
    ...(extra ?? {}),
  };
  log.info('STARTUP', 'Sardius CAFv3 receiver online', fields);
}
